import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ToastStack = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 220px;

  @media (min-width: 768px) {
    right: 65px;
  }
`;

const ToastCard = styled.div<{ $compact?: boolean }>`
  background: #fff;
  border: 1px solid var(--neutral-4);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: ${({ $compact }) => ($compact ? '8px 14px' : '12px 14px')};
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '4px' : '10px')};
`;

const CollapsedButton = styled(Button)`
  height: auto !important;
  padding: 10px 14px !important;
  display: flex !important;
  align-items: center !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16) !important;
  }
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const SummaryTitle = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const StyledIcon = styled.span`
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.88);
  font-size: 12px;
`;

const ToastHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Thumbnail = styled.div<{ $url?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background-color: var(--secondary-5);
  background-image: ${({ $url }) => ($url ? `url("${$url}")` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const ToastTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;

const ToastFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export type BatchCompletedStatus = 'completed' | 'completed_with_errors' | 'failed' | 'canceled';

export type BatchOperationItem = {
  id: string;
  title: string;
  thumbnail?: string;
  /** Progress for active batches - will display "Processing X/Y" */
  progress?: { current: number; total: number };
  /** Status and error count for completed batches */
  completedStatus?: { status: BatchCompletedStatus; failedCount?: number };
  isProcessing: boolean;
  buttons: {
    cancel?: {
      show: boolean;
      loading?: boolean;
      onClick: () => void | Promise<void>;
    };
    review?: {
      show: boolean;
      onClick: () => void;
    };
    dismiss?: {
      show: boolean;
      onClick: () => void;
    };
  };
};

type BatchToastProps = {
  batch: BatchOperationItem;
};

function BatchToast({ batch }: BatchToastProps) {
  const { t } = useTranslation();
  const [isCanceling, setIsCanceling] = useState(false);

  const handleCancel = async () => {
    if (!batch.buttons.cancel?.onClick) return;

    setIsCanceling(true);
    try {
      await batch.buttons.cancel.onClick();
    } finally {
      setIsCanceling(false);
    }
  };

  const getDisplayMessage = () => {
    if (batch.isProcessing && batch.progress) {
      return t('notifications:processing_progress', {
        current: batch.progress.current,
        total: batch.progress.total,
      });
    }

    if (!batch.completedStatus) {
      return t('notifications:process_completed');
    }

    switch (batch.completedStatus.status) {
      case 'completed_with_errors':
        return t('notifications:process_completed_with_errors', {
          failed: batch.completedStatus.failedCount ?? 0,
        });
      case 'failed':
        return t('notifications:process_failed');
      case 'canceled':
        return t('notifications:process_canceled');
      default:
        return t('notifications:process_completed');
    }
  };

  const displayMessage = getDisplayMessage();
  const hasThumbnail = Boolean(batch.thumbnail);

  return (
    <ToastCard $compact={!hasThumbnail}>
      <ToastHeader>
        {hasThumbnail ? <Thumbnail data-testid="batch-toast-thumbnail" $url={batch.thumbnail} /> : null}
        <ToastTitle>{batch.title}</ToastTitle>
      </ToastHeader>
      <ToastFooter>
        <ToastMessage>{displayMessage}</ToastMessage>
        {batch.buttons.cancel?.show && (
          <Button
            type="text"
            size="small"
            danger
            loading={batch.buttons.cancel.loading || isCanceling}
            onClick={() => void handleCancel()}
          >
            {t('buttons:cancel')}
          </Button>
        )}
        {batch.buttons.review?.show && (
          <Button type="primary" size="small" onClick={batch.buttons.review.onClick}>
            {t('vehicleScreen:bulk_enhance_review')}
          </Button>
        )}
        {batch.buttons.dismiss?.show && (
          <Button type="text" size="small" onClick={batch.buttons.dismiss.onClick}>
            {t('buttons:close')}
          </Button>
        )}
      </ToastFooter>
    </ToastCard>
  );
}

type CollapsedSummaryProps = {
  isExpanded: boolean;
  onToggle: () => void;
  summaryText: string;
};

const CollapsedSummary = ({ isExpanded, onToggle, summaryText }: CollapsedSummaryProps) => {
  return (
    <CollapsedButton onClick={onToggle} block>
      <SummaryHeader>
        <SummaryTitle>{summaryText}</SummaryTitle>
        <StyledIcon>{isExpanded ? <DownOutlined /> : <UpOutlined />}</StyledIcon>
      </SummaryHeader>
    </CollapsedButton>
  );
};

type Props = {
  batches: BatchOperationItem[];
  summaryText: string;
};

function BatchOperationToasts({ batches, summaryText }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!batches.length) {
    return null;
  }

  const hasMultipleBatches = batches.length > 1;

  return (
    <ToastStack>
      {hasMultipleBatches && !isExpanded ? (
        <CollapsedSummary
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
          summaryText={summaryText}
        />
      ) : (
        <>
          {hasMultipleBatches && (
            <CollapsedSummary
              isExpanded={isExpanded}
              onToggle={() => setIsExpanded(!isExpanded)}
              summaryText={summaryText}
            />
          )}
          {batches.map((batch) => (
            <BatchToast key={batch.id} batch={batch} />
          ))}
        </>
      )}
    </ToastStack>
  );
}

export default BatchOperationToasts;
